import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Observable, catchError, filter, finalize, of, switchMap, take } from "rxjs";
import { ModalDialog } from "../Modal/modal";
import { AuthService } from "../services/auth.service";
import { attachTokenToRequest } from "./interceptor.helper";

export const errorInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    let authService = inject(AuthService);
    let dialogService = inject(MatDialog);

    return next(request).pipe(
        catchError((err): Observable<any> => {
            return handleErrorStatus(err, request, next, authService, dialogService);
        })
    );
}

function handleErrorStatus(err: Observable<any>, request: HttpRequest<unknown>, next: HttpHandlerFn, authService: AuthService, dialogService: MatDialog) {
    if (err instanceof HttpErrorResponse) {
        let errorStatus = (<HttpErrorResponse>err).status;
        switch (errorStatus) {
            case 0:
                return handleHttpResponseZeroError(err, dialogService);
            case 401:
                return handleHttpResponseUnauthorizedError(request, next, authService);
            case 403:
                return handleHttpResponseForbiddenError(err, authService, dialogService);
            case 422:
                return handleHttpResponseUnprocessableError(err, dialogService);
            default:
                return handleError(err, dialogService);
        }
    } else {
        return handleError(err, dialogService);
    }
}

function handleHttpResponseZeroError(error, dialogService) {
    let message = 'Unable to reach the server. Please verify that your internet connection is stable and running!'

    const dialogRef = dialogService.open(ModalDialog, {
        data: {
            title: 'Error',
            message: message,
            showCancel: false,
            saveButtonText: 'Okay'
        }
    });
    dialogRef.disableClose = true;
    return of(error);
}

function handleHttpResponseUnauthorizedError(request: HttpRequest<any>, next: HttpHandlerFn, authService: AuthService) {
    // First thing to check if the token is in process of refreshing
    if (!authService.isTokenRefreshing)  // If the Token Refresheing is not true
    {
        authService.isTokenRefreshing = true;
        // Any existing value is set to null
        // Reset here so that the following requests wait until the token comes back from the refresh token API call
        authService.tokenSubject.next(null);

        /// call the API to refresh the token
        return authService.refreshToken().pipe(
            switchMap((tokenresponse: any) => {
                if (tokenresponse) {
                    authService.tokenSubject.next(tokenresponse.token);
                    authService.setSession(tokenresponse);
                    return next(attachTokenToRequest(request, tokenresponse.token));
                }
                return <any>authService.logoutWithoutUserPermission();
            }),
            finalize(() => {
                authService.isTokenRefreshing = false;
            })
        );
    }
    else {
        return authService.tokenSubject.pipe(
            filter(token => token != null),
            take(1),
            switchMap(
                token => {
                    return next(attachTokenToRequest(request, token));
                }));
    }
}

function handleHttpResponseForbiddenError(error, authService: AuthService, dialogService: MatDialog) {
    const currentDate = new Date().toLocaleString();
    let message = 'An authorization error occured at: ' + currentDate + ', local time.' +
        '\nPlease take a screenshot of this message and contact the support for further help.' +
        '\nTo prevent the application from running in an invalid state you will be logged out now.';
    const dialogRef = dialogService.open(ModalDialog, {
        data: {
            title: 'Error',
            message: message,
            showCancel: false,
            saveButtonText: 'Okay'
        }
    });
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe(result => {
        authService.logoutWithoutUserPermission();
    });
    return of(error);
}

function handleHttpResponseUnprocessableError(error, dialogService: MatDialog) {
    let message = '';
    error.error.text().then(text => {
        message = text;
        const dialogRef = dialogService.open(ModalDialog, {
            data: {
                title: 'Warning',
                message: message,
                showCancel: false,
                saveButtonText: 'Okay'
            }
        });
        dialogRef.disableClose = true;
    });
    return of(error);
}


// Global error handler method 
function handleError(error, dialogService: MatDialog) {
    const currentDate = new Date().toLocaleString();
    let message = 'An error occured at: ' + currentDate + ', local time.' +
        "\nPlease take a screenshot of this message in case the application keeps working inapropriately and contact the support for further help.";
    const dialogRef = dialogService.open(ModalDialog, {
        data: {
            title: 'Error',
            message: message,
            showCancel: false,
            saveButtonText: 'Okay'
        }
    });
    dialogRef.disableClose = true;
    return of(error);
}